<template>
    <div v-if="showModal">
        <div class="backdrop" @click="close"></div>
        <div class="card card-outline card-primary card-box mx-auto">

            <div class="card-header">
                <h3 class="card-title">Mettre fin à la dispense par anticipation</h3>
                <div class="card-tools">
                    <button type="button" class="btn btn-tool" @click="close">
                        <i class="fas fa-times"></i>
                    </button>
                </div>
            </div>

            <div class="modal-card-body">
                <label for="early-termination-reason">Motif de fin anticipée</label>
                <select
                    v-if="reasons.length > 0"
                    id="early-termination-reason"
                    class="form-control"
                    v-model="selectedReasonId"
                >
                    <option v-for="reason in reasons" :key="reason.id" :value="reason.id">
                        {{ reason.label }}
                    </option>
                </select>
                <textarea
                    v-show="false"
                    class="form-control mt-2"
                    name="early-termination-reason"
                    :class="{ 'is-invalid': submitted && !isReasonValid }"
                    v-model="earlyTerminationReason"
                    placeholder="Saisissez le motif personnalisé..."
                    rows="3"
                ></textarea>
                <FormError error="Veuillez sélectionner un motif ou saisir un motif personnalisé" v-if="submitted && !isReasonValid" />

                <label for="early-termination-end-date" class="mt-3">Date de fin</label>
                <input
                    type="date"
                    id="early-termination-end-date"
                    class="form-control"
                    :class="{ 'is-invalid': submitted && !isEndDateValid }"
                    :min="minEndDate"
                    :max="maxEndDate"
                    v-model="earlyTerminationEndDate"
                />
                <FormError error="Veuillez renseigner la date de fin" v-if="submitted && !earlyTerminationEndDate" />
                <FormError error="La date de fin anticipée ne peut pas être antérieure à la date de début de dispense." v-if="submitted && earlyTerminationEndDate && minEndDate && earlyTerminationEndDate < minEndDate" />
                <FormError :error="`La date de fin ne peut pas dépasser le ${maxEndDateFormatted}.`" v-if="submitted && earlyTerminationEndDate && earlyTerminationEndDate > maxEndDate" />
            </div>

            <div class="modal-card-actions text-right">
                <button
                    class="btn btn-primary button-blue"
                    :disabled="isLoading"
                    @click.prevent="terminate"
                >
                    <span>Confirmer</span>
                    <span
                        v-if="isLoading"
                        class="spinner-border spinner-border-sm ml-2"
                        role="status"
                        aria-hidden="true"
                    ></span>
                </button>
            </div>

        </div>
    </div>
</template>

<script>
    import FormError from '../FormError';
    import { fetchEarlyTerminationReasons, terminateEarlyExemptionById } from '@/service/api/exemptions.api';
    import { successMessage, errorMessage, messageTexts } from '@/utils/sweet-alert-options';

    export default {
        props: {
            showModal: Boolean,
            exemption: Object,
        },
        data() {
            return {
                selectedReasonId: null,
                baseReason: '',
                earlyTerminationReason: '',
                earlyTerminationEndDate: '',
                reasons: [],
                isLoading: false,
                submitted: false,
            };
        },
        computed: {
            minEndDate() {
                return this.exemption?.startDate?.slice(0, 10) ?? null;
            },
            maxEndDate() {
                const expDate = this.exemption?.exemptionReason?.expirationDate?.slice(0, 10);
                return expDate ?? new Date().toISOString().slice(0, 10);
            },
            maxEndDateFormatted() {
                return new Date(this.maxEndDate).toLocaleDateString('fr-FR');
            },
            isReasonValid() {
                return this.earlyTerminationReason.trim() !== '';
            },
            isEndDateValid() {
                if (!this.earlyTerminationEndDate) {
                  return false;
                }

                if (this.minEndDate && this.earlyTerminationEndDate < this.minEndDate) {
                  return false;
                }

                return this.earlyTerminationEndDate <= this.maxEndDate;
            },
            isFormValid() {
                return this.isReasonValid && this.isEndDateValid;
            },
        },
        watch: {
            async showModal(value) {
                if (value) {
                    await this.loadReasons();
                }
            },
            selectedReasonId() {
                const reason = this.reasons.find(({ id }) => id === this.selectedReasonId);
                if (!reason) {
                  return;
                }

                if (this.baseReason === this.earlyTerminationReason || confirm('Choisir ce motif ? ceci remplacera le motif actuel.')) {
                  this.baseReason = this.earlyTerminationReason = reason.label;
                }
            }
        },
        methods: {
            async loadReasons() {
                const { data } = await fetchEarlyTerminationReasons();
                this.reasons = data;
            },
            async terminate() {
                this.submitted = true;
                if (!this.isFormValid) {
                    return;
                }
                this.isLoading = true;
                try {
                    const earlyTerminationReason = this.earlyTerminationReason.trim();
                    await terminateEarlyExemptionById(this.exemption.id, earlyTerminationReason, this.earlyTerminationEndDate);
                    this.isLoading = false;
                    this.$swal({ title: messageTexts.earlyTerminationSuccess || 'Fin anticipée enregistrée', ...successMessage });
                    this.$emit('terminateEarlySuccess');
                    this.close();
                } catch ({ response }) {
                    this.isLoading = false;
                    let message;
                    if (response?.data?.violations?.length) {
                        message = response.data.violations.map(violation => violation.title).join('<br>');
                    } else {
                        message = response?.data?.detail ?? 'Une erreur est survenue lors de l’enregistrement de la fin anticipée.';
                    }
                    this.$swal({ html: message, ...errorMessage });
                }
            },
            close() {
                if (this.isLoading) {
                    return;
                }
                this.selectedReasonId = null;
                this.earlyTerminationReason = '';
                this.earlyTerminationEndDate = '';
                this.baseReason = '';
                this.reasons = [];
                this.submitted = false;
                this.$emit('update:showModal', false);
            },
        },
        components: {
            FormError,
        },
    };
</script>
